import { BoxType, CustomTemplateBox, GroupPeopleType, ImageFrameRole } from '@lite-workspace/dto-fotoclass-templates';
import { BookData } from '../interfaces/book-data.interface';
import { PersistedSpreadV1 } from '../normalizer/types';
import { CollageData } from '../interfaces/collage-data.interface';
import { SpreadData } from '../interfaces/spread-data.interface';
import { DrawableData, DrawableGroupData, isDrawableGroup, isDrawableImage } from '../interfaces/drawable-data.interface';
import { HydratedCellLike, hydratedRosterCellPredicate, isRosterSurface } from './roster-seating-queries';
import { findDrawableDeep } from './drawable-tree';

/**
 * Page numbering shared by every consumer of this report: the cover is page 0 and the content
 * spreads follow as 1…N — the same order the editor's spread strip and `initialSpreadIndex` use.
 */
export interface EmptyPhotoFramesReport {
  /** Total empty photo frames across the cover and every spread. */
  readonly count: number;
  /** Ascending page indexes that hold at least one empty frame (cover 0, spreads 1…N). */
  readonly spreadIndexes: readonly number[];
}

export const NO_EMPTY_PHOTO_FRAMES: EmptyPhotoFramesReport = { count: 0, spreadIndexes: [] };

/**
 * Почему рамка не встанет в печать. `empty` — дыра как сегодня; `not_loaded` — фото поставлено, но
 * его серверные версии ещё не нарезаны (в рамке лежит локальный `blob:` либо снимок без thumb/small);
 * `load_error` — обработка этого фото упала в этом сеансе.
 *
 * Сеансовая природа двух новых причин: `persistablePayload` вычищает blob-ячейки перед PATCH, поэтому
 * после перезагрузки такая рамка сохранена ПУСТОЙ и классифицируется как `empty`.
 */
export type FrameProblemKind = 'empty' | 'not_loaded' | 'load_error' | 'stale';

/** A picture address that lives in ONE browser tab: readable here, an empty frame everywhere else. */
export function isBlobImageRef(url: string | null | undefined): boolean {
  return typeof url === 'string' && url.startsWith('blob:');
}

export interface FrameProblemReport extends EmptyPhotoFramesReport {
  /** Идентификаторы боксов в порядке обхода — превью обводит ровно их. */
  readonly boxIds: readonly string[];
}

export interface FrameProblemOptions {
  /** Фото, чья обработка упала в этом сеансе (`processingFailedPhotoIds` стора). */
  readonly failedPhotoIds?: ReadonlySet<string>;
  /** Фото, чья blob-ссылка пришла С СЕРВЕРА: сеанс её не резолвит, «Загружается…» было бы навсегда. */
  readonly stalePhotoIds?: ReadonlySet<string>;
  /**
   * Is the cover a strip tile (`capabilitiesFor(kind).coverTile`)? `false` → the cover is not scanned and
   * spreads number from 0, so every index is the strip position the editor jumps to (`coverTileOffset`).
   */
  readonly coverTile?: boolean;
}

/** The cover as page 0 only where it is a strip tile — the same rule as the editor's `coverTileOffset`. */
function coverTileOf(
  book: Partial<Pick<BookData, 'cover'>>,
  opts: Pick<FrameProblemOptions, 'coverTile'>,
): BookData['cover'] | undefined {
  return opts.coverTile === false ? undefined : book.cover;
}

const NO_FRAME_PROBLEMS: FrameProblemReport = { count: 0, spreadIndexes: [], boxIds: [] };

export interface FrameProblemsReport {
  readonly empty: FrameProblemReport;
  readonly notLoaded: FrameProblemReport;
  readonly loadError: FrameProblemReport;
  /** Blob-ссылка, которую в этом сеансе никто не ждёт: пришла с сервера и не сменится никогда. */
  readonly stale: FrameProblemReport;
}

/** A spread or the cover; `spread_type` optional so bare-collage callers stay expressible. */
type PageLike = (Pick<SpreadData, 'collages'> & Partial<Pick<SpreadData, 'spread_type'>>) | null | undefined;

/**
 * Набор ячеек, которые наполняет гидрация, — ровно то, что счётчику пустых рамок считать нельзя:
 * `applyVignetteImageToGroup` ставит кадр из карточки персоны в ПОСАЖЕННУЮ ростерную ячейку на ЛЮБОЙ
 * поверхности, а в сохранённой модели у неё `image: null` по построению.
 */
function hydratedCellPredicate(page: PageLike): (group: HydratedCellLike) => boolean {
  return hydratedRosterCellPredicate(page ?? { collages: [] }, isRosterSurface(page));
}

/**
 * A vignette person-slot group. Its DIRECT image children are owned by the persons pipeline end to
 * end: generation (`assignPersonsToGroups`) writes only `params.personId` + texts, and every render
 * path — the editor (`hydrateLoadedPersons`) and the print/preview callback alike — injects the
 * portrait at runtime via `applyPersonsToVignetteModel`/`applyVignetteImageToGroup`. The persisted
 * model therefore keeps `image: null` there BY DESIGN, and counting those cells refused perfectly
 * complete albums (the «Химия» false block: 10 «empty frames» on a fully filled vignette).
 *
 * ЛЕГАСИ-ВЫЧЕТ, шире набора гидрации: сюда попадают и рамка владельца, и ячейка с `personId` без роли.
 * Держится только на виньетках и только затем, чтобы уже собранные книги не начали получать отказ по
 * рамкам, которые сегодня не считаются. `GroupPeopleType.FREE` — организационная роль
 * («исключена из виньеточных расчётов»), она не гидрируется, и её рамки остаются счётными.
 *
 * Scope boundary: a «phantom vignette» cell — a person group whose person is missing or has no
 * portrait — prints empty too, but that is a separate bug class this gate deliberately does not
 * catch (the same way face-on-fold exclusions are out of this gate's scope).
 */
function isVignettePersonGroup(drawable: DrawableGroupData): boolean {
  const params = drawable.params as { personId?: string | null; peopleType?: string | null } | undefined;
  if (!params) return false;
  if (params.personId != null) return true;
  return params.peopleType != null && params.peopleType !== GroupPeopleType.FREE;
}

/**
 * THE definition of "a photo frame is empty" — shared verbatim by the album editor, the approval
 * screen and the server gates, exactly like `missingCoverTextTypes` is for cover text. A frame is
 * empty when it carries no image id and no frame role. No decorative preview a frame may be showing
 * is ever consulted — it is not the reader's photo and prints as a placeholder.
 *
 * A hidden or non-rendered frame never reaches paper, so it cannot leave a hole in the printed book
 * — counting it would block an album that prints perfectly (same walk the cover-text gate does).
 *
 * A role-marked frame (S12) is fed only by its role's photo pool; when the pool has nothing fitting
 * the frame legally ships empty — it is an occupied design slot, not a hole, so it neither blocks
 * approval nor lights up in the empty-frames navigator. A frame blanked by a dispute («это не я»)
 * is the exception under ANY role: its photo was already seated by the assembly and nobody will
 * refill it, so it is a hole the reader must close.
 */
export function isEmptyPhotoFrame(drawable: DrawableData): boolean {
  return isDrawableImage(drawable) && !drawable.image?.id && (!drawable.frameRole || !!drawable.disputedPhoto);
}

/**
 * Одна причина на рамку, в порядке приоритета: дыра важнее незагруженного фото, а упавшая обработка
 * важнее ожидания. «Ещё не нарезано» опознаётся ТОЛЬКО по локальной ссылке `blob:` в рамке: нормализатор
 * (`assigned-photo.ts`) отдаёт `thumb`/`small` как `undefined` всякий раз, когда у снимка нет
 * `versions.thumb` и рендишенов, так что по их отсутствию здоровые сохранённые рамки читались бы как
 * «Загружается…».
 */
export function classifyPhotoFrame(drawable: DrawableData, opts: FrameProblemOptions = {}): FrameProblemKind | null {
  if (isEmptyPhotoFrame(drawable)) return 'empty';
  if (!isDrawableImage(drawable)) return null;

  const image = drawable.image;
  if (!image?.id) return null;
  if (opts.failedPhotoIds?.has(image.id)) return 'load_error';
  if (!isBlobImageRef(image.image)) return null;
  return opts.stalePhotoIds?.has(image.id) ? 'stale' : 'not_loaded';
}

/** Классификатор одной рамки, зашитый в обход: сегодняшний счёт пустых рамок — его частный случай. */
type FrameClassifier = (drawable: DrawableData) => FrameProblemKind | null;

const EMPTY_ONLY: FrameClassifier = drawable => (isEmptyPhotoFrame(drawable) ? 'empty' : null);

/** На обложке пустая рамка роли — не законный пустой слот, а самая видная дыра книги: роль её не извиняет. */
const onCover =
  (classify: FrameClassifier): FrameClassifier =>
  drawable =>
    isDrawableImage(drawable) && !drawable.image?.id ? 'empty' : classify(drawable);

/**
 * Truly recursive: `DrawableGroupData.drawableObjects` self-references, so a vignette nests person
 * groups inside person groups and a single-level walk under-counts (the reason `getAllDrawableImages`
 * must not back a gate).
 *
 * `skipDirectImages` marks the DIRECT children of a hydrated person group — exactly the set the
 * hydrator rewrites (`applyVignetteImageToGroup` maps one level, never descending). A nested group
 * inside a person group is NOT hydrated, so the walk resets to normal counting there.
 */
function collectEmptyFrameIdsInDrawables(
  drawables: readonly DrawableData[] | undefined,
  sink: Map<string, FrameProblemKind>,
  skipDirectImages: boolean,
  classify: FrameClassifier,
): void {
  for (const drawable of drawables ?? []) {
    if (drawable.visible === false || drawable.skipRender === true) continue;

    if (isDrawableGroup(drawable)) {
      collectEmptyFrameIdsInDrawables(drawable.drawableObjects, sink, false, classify);
      continue;
    }

    if (skipDirectImages && isDrawableImage(drawable)) continue;
    const kind = classify(drawable);
    if (kind) sink.set(drawable.id, kind);
  }
}

/**
 * Only TOP-LEVEL collage groups can be person slots: `applyPersonsToCollage` maps the collage's own
 * `drawableObjects` and never recurses, so a deeper group carrying person params is not hydrated.
 */
function collectEmptyFrameIdsInCollage(
  collage: CollageData,
  sink: Map<string, FrameProblemKind>,
  vignettePage: boolean,
  isHydratedCell: (group: HydratedCellLike) => boolean,
  classify: FrameClassifier,
): void {
  for (const drawable of collage.drawableObjects ?? []) {
    if (drawable.visible === false || drawable.skipRender === true) continue;

    if (isDrawableGroup(drawable)) {
      const hydrated = isHydratedCell(drawable) || (vignettePage && isVignettePersonGroup(drawable));
      collectEmptyFrameIdsInDrawables(drawable.drawableObjects, sink, hydrated, classify);
      continue;
    }

    const kind = classify(drawable);
    if (kind) sink.set(drawable.id, kind);
  }
}

/**
 * The ids behind the count, in walk order — the editor highlights (wash + plus) exactly the frames
 * the gate counts, so a page can never be outlined over cells the approval would actually accept.
 */
export function collectEmptyPhotoFrameIdsOnPage(page: PageLike, cover = false): string[] {
  return [...collectFrameProblemsOnSurface(page, EMPTY_ONLY, cover).keys()];
}

/**
 * Одна развилка «обложка или разворот» для гейта и для обводки редактора: обложка идёт голым списком
 * коллажей под `onCover`, разворот — страницей, поэтому редактор не может разойтись с готовностью.
 */
export function collectFrameProblemsOnSurface(
  page: PageLike,
  classify: FrameClassifier,
  cover: boolean,
): Map<string, FrameProblemKind> {
  return cover ? collectFrameProblemsInCollages(page?.collages, onCover(classify)) : collectFrameProblemsOnPage(page, classify);
}

/**
 * Тот же обход, но с причиной у каждой рамки — превью подписывает ровно то, что рельса считает.
 * Порядок вставки = порядок обхода, как и у списка пустых рамок.
 */
export function collectFrameProblemsOnPage(page: PageLike, classify: FrameClassifier): Map<string, FrameProblemKind> {
  const sink = new Map<string, FrameProblemKind>();
  const vignettePage = isRosterSurface(page);
  const isHydratedCell = hydratedCellPredicate(page);
  for (const collage of page?.collages ?? []) {
    collectEmptyFrameIdsInCollage(collage, sink, vignettePage, isHydratedCell, classify);
  }

  return sink;
}

/** Empty photo frames on one page (a spread or the cover); a page without collages contributes none. */
export function countEmptyPhotoFramesOnPage(page: PageLike): number {
  return collectEmptyPhotoFrameIdsOnPage(page).length;
}

/**
 * Same walk over a bare collage list — for callers that hold collages rather than a page. Вычет
 * гидрируемых ячеек считается по составу рамок и потому работает и здесь; легаси-вычет виньетки без
 * страницы не применяется, так что страницу передавайте, когда она есть.
 */
export function countEmptyPhotoFramesInCollages(collages: readonly CollageData[] | undefined): number {
  return collectFrameProblemsInCollages(collages, EMPTY_ONLY).size;
}

/** Тот же обход по голому списку коллажей — для обложки, у которой страницы как таковой нет. */
export function collectFrameProblemsInCollages(
  collages: readonly CollageData[] | undefined,
  classify: FrameClassifier,
): Map<string, FrameProblemKind> {
  const sink = new Map<string, FrameProblemKind>();
  const isHydratedCell = hydratedRosterCellPredicate({ collages: collages ?? [] }, false);
  for (const collage of collages ?? []) collectEmptyFrameIdsInCollage(collage, sink, false, isHydratedCell, classify);
  return sink;
}

/**
 * The page-level inverse of the empty-frame walk: true when the page will render with at least
 * one real photo. An image counts only when it carries an assigned photo (`image.id`) — a
 * template placeholder (`image: null`) is an unfilled frame, not content. The walk is recursive
 * through groups because a design-pack spread keeps its photos inside group boxes, and it keeps
 * the gate's carve-out: a hydrated person cell gets its portrait at render, so it counts as a photo
 * even though the persisted model keeps `image: null` there by design.
 */
export function pageHasRenderablePhoto(page: PageLike): boolean {
  const vignettePage = isRosterSurface(page);
  const isHydratedCell = hydratedCellPredicate(page);
  for (const collage of page?.collages ?? []) {
    for (const drawable of collage.drawableObjects ?? []) {
      if (drawable.visible === false || drawable.skipRender === true) continue;

      if (isDrawableGroup(drawable)) {
        if (isHydratedCell(drawable) || (vignettePage && isVignettePersonGroup(drawable))) return true;
        if (drawablesHaveRenderablePhoto(drawable.drawableObjects)) return true;
        continue;
      }

      if (isDrawableImage(drawable) && !!drawable.image?.id) return true;
    }
  }

  return false;
}

function drawablesHaveRenderablePhoto(drawables: readonly DrawableData[] | undefined): boolean {
  for (const drawable of drawables ?? []) {
    if (drawable.visible === false || drawable.skipRender === true) continue;

    if (isDrawableGroup(drawable)) {
      if (drawablesHaveRenderablePhoto(drawable.drawableObjects)) return true;
      continue;
    }

    if (isDrawableImage(drawable) && !!drawable.image?.id) return true;
  }

  return false;
}

/**
 * Тот же вопрос «есть ли на поверхности хоть одно живое фото», но по СОХРАНЁННОЙ модели v1
 * (`collages[].boxes`), а не по v0-`drawableObjects`, которые читает `pageHasRenderablePhoto`:
 * редактор держит разворот именно в этой форме, и дроп на пустой разворот спрашивает её.
 * Живое фото = картиночный бокс с `params.assignedPhoto.photoId`; обход рекурсивный, потому что
 * дизайн-пак прячет кадры внутри групп.
 */
export function spreadHasRenderablePhoto(spread: Pick<PersistedSpreadV1, 'collages'> | null | undefined): boolean {
  return (spread?.collages ?? []).some(collage => boxesHaveAssignedPhoto(collage.boxes));
}

function boxesHaveAssignedPhoto(boxes: readonly CustomTemplateBox[] | undefined): boolean {
  for (const box of boxes ?? []) {
    if (box.type === BoxType.GROUP) {
      if (boxesHaveAssignedPhoto(box.boxes)) return true;
      continue;
    }

    if (box.type === BoxType.IMAGE && !!box.params.assignedPhoto?.photoId) return true;
  }

  return false;
}

/**
 * The whole book, cover included — an empty frame on the cover is the most visible hole of all, so it
 * is counted alongside the spreads and reported as page 0. Ростерные ячейки обложки из счёта выпадают
 * так же, как на любой другой поверхности: гидрация наполняет их из карточек персон.
 *
 * A missing book is NOT "no empty frames": callers gate on a model they could actually read and must
 * treat `null` as their own fail-closed case rather than reading a zero out of this function.
 */
export function findEmptyPhotoFrames(book: Pick<BookData, 'spreads'> & Partial<Pick<BookData, 'cover'>>): EmptyPhotoFramesReport {
  const pages = findEmptyPhotoFramePages(book);
  return {
    count: pages.reduce((sum, page) => sum + page.count, 0),
    spreadIndexes: pages.map(page => page.pageIndex),
  };
}

/**
 * Развороты, что напечатаются чистым листом: ни на одной странице нет бокса, кроме фона. Текст или декор —
 * уже содержимое; пустая рамка — тоже (её зовёт `findEmptyPhotoFrames`). `isSpreadEmpty` не годится: он
 * считает пустым разворот из одних пустых рамок, текста без букв или декора. Обложку не судит никогда.
 * Индексы — места в списке разворотов, 0…N-1.
 */
export function findEmptySpreads(book: Pick<BookData, 'spreads'>): readonly number[] {
  const blank: number[] = [];
  (book.spreads ?? []).forEach((spread, index) => {
    if (!spread.collages?.some(collage => collage.drawableObjects?.some(isSpreadContent))) blank.push(index);
  });
  return blank;
}

/** Фон и декор, снятый с плитки фона (`wornFromBackground`), — часть фона, а не содержимое разворота. */
function isSpreadContent(drawable: DrawableData): boolean {
  return drawable.type !== 'background' && drawable.wornFromBackground !== true;
}

/** Страница с дырами и сколько их на ней — адрес в нумерации {@link findEmptyPhotoFrames}. */
export interface EmptyPhotoFramesPage {
  readonly pageIndex: number;
  readonly count: number;
}

/**
 * Тот же обход, но с разбивкой по страницам: рельса готовности печатает КАРТОЧКУ НА СТРАНИЦУ, и общий
 * счёт `findEmptyPhotoFrames` — сумма этих строк, а не второй проход по книге.
 */
export function findEmptyPhotoFramePages(
  book: Pick<BookData, 'spreads'> & Partial<Pick<BookData, 'cover'>>,
  opts: Pick<FrameProblemOptions, 'coverTile'> = {},
): readonly EmptyPhotoFramesPage[] {
  const pages: EmptyPhotoFramesPage[] = [];

  const cover = coverTileOf(book, opts);
  const coverPresent = Boolean(cover);
  if (coverPresent) {
    const coverEmpty = collectEmptyPhotoFrameIdsOnPage(cover, true).length;
    if (coverEmpty > 0) pages.push({ pageIndex: 0, count: coverEmpty });
  }

  const offset = coverPresent ? 1 : 0;
  (book.spreads ?? []).forEach((spread, index) => {
    const empty = countEmptyPhotoFramesOnPage(spread);
    if (empty > 0) pages.push({ pageIndex: offset + index, count: empty });
  });

  return pages;
}

/**
 * Все три причины одним обходом книги, обложка включена, — рельса и превью читают ОДИН отчёт, поэтому
 * точка на чипе, строка списка и обводка не могут разойтись. Нумерация страниц та же, что у
 * `findEmptyPhotoFrames`: обложка 0, развороты 1…N.
 */
export function findFrameProblems(
  book: Pick<BookData, 'spreads'> & Partial<Pick<BookData, 'cover'>>,
  opts: FrameProblemOptions = {},
): FrameProblemsReport {
  const classify: FrameClassifier = drawable => classifyPhotoFrame(drawable, opts);
  const buckets: Record<FrameProblemKind, { count: number; spreadIndexes: number[]; boxIds: string[] }> = {
    empty: { count: 0, spreadIndexes: [], boxIds: [] },
    not_loaded: { count: 0, spreadIndexes: [], boxIds: [] },
    load_error: { count: 0, spreadIndexes: [], boxIds: [] },
    stale: { count: 0, spreadIndexes: [], boxIds: [] },
  };

  const absorb = (found: Map<string, FrameProblemKind>, pageIndex: number): void => {
    for (const [boxId, kind] of found) {
      const bucket = buckets[kind];
      bucket.count += 1;
      bucket.boxIds.push(boxId);
      if (bucket.spreadIndexes[bucket.spreadIndexes.length - 1] !== pageIndex) bucket.spreadIndexes.push(pageIndex);
    }
  };

  const cover = coverTileOf(book, opts);
  const coverPresent = Boolean(cover);
  if (coverPresent) absorb(collectFrameProblemsOnSurface(cover, classify, true), 0);

  const offset = coverPresent ? 1 : 0;
  (book.spreads ?? []).forEach((spread, index) => absorb(collectFrameProblemsOnPage(spread, classify), offset + index));

  const report = (kind: FrameProblemKind): FrameProblemReport => (buckets[kind].count === 0 ? NO_FRAME_PROBLEMS : buckets[kind]);

  return { empty: report('empty'), notLoaded: report('not_loaded'), loadError: report('load_error'), stale: report('stale') };
}

/** Beyond this stretch the print loses sharpness — the legacy `finishBookForLab` threshold («более чем на 70 %»). */
export const UPSCALE_WARNING_RATIO = 1.7;

/** Photo drawn larger than its pixels, by the ratio: the placed rect is book px at print dpi, `image.width` the file's. */
export function isUpscaledPhotoFrame(drawable: DrawableData): boolean {
  if (!isDrawableImage(drawable) || !drawable.image?.width || !drawable.naturalAligns?.image?.width) return false;
  return drawable.naturalAligns.image.width / drawable.image.width > UPSCALE_WARNING_RATIO;
}

/** Pages holding at least one upscaled photo, in the same numbering as {@link findEmptyPhotoFramePages}. */
export function findUpscaledPhotoPages(
  book: Pick<BookData, 'spreads'> & Partial<Pick<BookData, 'cover'>>,
  opts: Pick<FrameProblemOptions, 'coverTile'> = {},
): readonly number[] {
  const pageHasUpscaled = (collages: readonly CollageData[] | undefined): boolean =>
    (collages ?? []).some(collage => Boolean(findDrawableDeep(collage.drawableObjects ?? [], isUpscaledPhotoFrame)));

  const pages: number[] = [];
  const cover = coverTileOf(book, opts);
  if (cover && pageHasUpscaled(cover.collages)) pages.push(0);

  const offset = cover ? 1 : 0;
  (book.spreads ?? []).forEach((spread, index) => {
    if (pageHasUpscaled(spread.collages)) pages.push(offset + index);
  });

  return pages;
}

/** A cover that prints as a blank sheet: no drawable at all on any of its collages. */
export function isCoverBlank(book: Partial<Pick<BookData, 'cover'>>): boolean {
  return !(book.cover?.collages ?? []).some(collage => (collage.drawableObjects ?? []).length > 0);
}

/**
 * Роли пустых рамок обложки, по рамке на элемент: дыру с ролью видит только `onCover`, на развороте роль
 * её извиняет. Обход тот же, что у гейта: ростерные ячейки обложки не в счёте.
 */
export function emptyRoleFramesOnCover(book: Partial<Pick<BookData, 'cover'>>): ImageFrameRole[] {
  const roles: ImageFrameRole[] = [];
  collectFrameProblemsInCollages(book.cover?.collages, drawable => {
    if (!isDrawableImage(drawable) || drawable.image?.id || !drawable.frameRole) return null;
    roles.push(drawable.frameRole);
    return 'empty';
  });
  return roles;
}

/** На обложке есть пустая рамка с ролью — по ней отказ утверждения возвращает альбом фотографу. */
export function hasEmptyRoleFrameOnCover(book: Partial<Pick<BookData, 'cover'>>): boolean {
  return emptyRoleFramesOnCover(book).length > 0;
}
